import axios, { type AxiosInstance } from 'axios';

import type { GearInventory, GearInventoryRequest } from './models/GearInventory';
import type {
  GearCatalogFilters,
  GearItem,
  GearItemAvailabilityQuery,
  GearItemRequest,
  UpdateGearItemRequest,
} from './models/GearItem';
import type { CreateGearRentalRequest, GearRental } from './models/GearRental';
import { applyClientChannelHeader } from './utils/clientChannel';

const publicBaseURL = process.env.REACT_APP_API_BASE_URL;
const userBaseURL = process.env.REACT_APP_API_BASE_URL + '/user';
const hostBaseURL = process.env.REACT_APP_API_BASE_URL + '/host';

class GearApiClient {
  private readonly publicAxios: AxiosInstance;
  private readonly userAxios: AxiosInstance;
  private readonly hostAxios: AxiosInstance;

  constructor() {
    this.publicAxios = this.createPublicAxiosInstance();
    this.userAxios = this.createAuthenticatedAxiosInstance(userBaseURL, 'X-UserId', 'assumed_user_id');
    this.hostAxios = this.createAuthenticatedAxiosInstance(
      hostBaseURL,
      'X-OrganizationId',
      'assumed_organization_id',
    );
  }

  private createPublicAxiosInstance(): AxiosInstance {
    const instance = axios.create({
      baseURL: publicBaseURL,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    instance.interceptors.request.use(
      (config) => {
        applyClientChannelHeader(config.headers);
        return config;
      },
      (error) => Promise.reject(error),
    );

    return instance;
  }

  private createAuthenticatedAxiosInstance(
    baseURL: string,
    headerName: 'X-UserId' | 'X-OrganizationId',
    storageKey: 'assumed_user_id' | 'assumed_organization_id',
  ): AxiosInstance {
    const instance = axios.create({
      baseURL,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    instance.interceptors.request.use(
      (config) => {
        const accessToken = localStorage.getItem('access_token');
        const assumedId = localStorage.getItem(storageKey);

        if (accessToken) {
          config.headers['Authorization'] = `Bearer ${accessToken}`;
        }

        if (assumedId) {
          config.headers[headerName] = assumedId;
        }

        applyClientChannelHeader(config.headers);

        return config;
      },
      (error) => Promise.reject(error),
    );

    instance.interceptors.response.use(
      (response) => response,
      (error) => this.handleErrorResponse(error),
    );

    return instance;
  }

  private handleErrorResponse(error: any): Promise<never> {
    const status = error.response ? error.response.status : null;

    if (status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('phone_number');
    }

    return Promise.reject(error);
  }

  public async getGearItems(filters: GearCatalogFilters = {}): Promise<GearItem[]> {
    const response = await this.publicAxios.get<GearItem[]>('/gear/items', {
      params: filters,
    });
    return response.data;
  }

  public async getGearItem(id: string, dates?: GearItemAvailabilityQuery): Promise<GearItem> {
    const response = await this.publicAxios.get<GearItem>(`/gear/items/${id}`, {
      params: dates,
    });
    return response.data;
  }

  public async createGearRental(request: CreateGearRentalRequest): Promise<GearRental> {
    const response = await this.userAxios.post<GearRental>('/gear-rentals', request);
    return response.data;
  }

  public async getUserGearRentals(): Promise<GearRental[]> {
    const response = await this.userAxios.get<GearRental[]>('/gear-rentals');
    return response.data;
  }

  public async cancelUserGearRental(id: string): Promise<void> {
    await this.userAxios.delete(`/gear-rentals/${id}`);
  }

  public async getHostGearInventory(): Promise<GearInventory | null> {
    try {
      const response = await this.hostAxios.get<GearInventory>('/gear-inventory');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  public async upsertHostGearInventory(request: GearInventoryRequest): Promise<GearInventory> {
    const response = await this.hostAxios.put<GearInventory>('/gear-inventory', request);
    return response.data;
  }

  public async getHostGearItems(): Promise<GearItem[]> {
    try {
      const response = await this.hostAxios.get<GearItem[]>('/gear-inventory/items');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return [];
      }
      throw error;
    }
  }

  public async createHostGearItem(request: GearItemRequest): Promise<GearItem> {
    const response = await this.hostAxios.post<GearItem>('/gear-inventory/items', request);
    return response.data;
  }

  public async updateHostGearItem(id: string, request: UpdateGearItemRequest): Promise<GearItem> {
    const response = await this.hostAxios.put<GearItem>(`/gear-inventory/items/${id}`, request);
    return response.data;
  }

  public async archiveHostGearItem(id: string): Promise<void> {
    await this.hostAxios.delete(`/gear-inventory/items/${id}`);
  }

  public async getHostGearRentals(): Promise<GearRental[]> {
    const response = await this.hostAxios.get<GearRental[]>('/gear-rentals');
    return response.data;
  }

  public async getHostGearRental(id: string): Promise<GearRental> {
    const response = await this.hostAxios.get<GearRental>(`/gear-rentals/${id}`);
    return response.data;
  }

  public async confirmHostGearRental(id: string): Promise<void> {
    await this.hostAxios.post(`/gear-rentals/${id}/confirm`);
  }

  public async completeHostGearRental(id: string): Promise<void> {
    await this.hostAxios.post(`/gear-rentals/${id}/complete`);
  }

  public async cancelHostGearRental(id: string): Promise<void> {
    await this.hostAxios.delete(`/gear-rentals/${id}`);
  }
}

export const gearApiClient = new GearApiClient();
export default gearApiClient;
